import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Space, Table, Tag, Typography, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import type { CardGenerationConfig } from '@/types';
import {
  CardRarityOdds,
  RARITY_LABELS,
  oneInN,
  rarityOddsProblem,
  rarityOddsTotal,
} from './cardRarityOdds';

const { Title, Text } = Typography;

const ROWS = [
  { rarity: '0', color: 'default' },
  { rarity: '1', color: 'blue' },
  { rarity: '2', color: 'purple' },
  { rarity: '3', color: 'gold' },
];

/**
 * Config → Card Generation: the odds a card made without a set rarity (match drops, level-up
 * player drops, stump rewards, store items with no card rarity) comes out at each rarity.
 */
export const CardRarityOddsSection: React.FC = () => {
  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const [odds, setOdds] = useState<CardRarityOdds>({});
  const [saved, setSaved] = useState<CardRarityOdds>({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getCardGenerationConfig();
      const weights = (res.data as CardGenerationConfig)?.cardRarityWeights;
      if (!weights) {
        setLoadError(
          'This backend does not have editable rarity odds yet (it still uses 60/25/12/3).'
        );
        return;
      }
      setOdds(weights);
      setSaved(weights);
      setLoaded(true);
    } catch {
      setLoadError('Could not load the rarity odds.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = canEdit?.can !== false && loaded;
  const problem = rarityOddsProblem(odds);
  const changed = ROWS.some((r) => odds[r.rarity] !== saved[r.rarity]);
  const total = rarityOddsTotal(odds);

  const handleSave = () => {
    if (problem) {
      message.error(problem);
      return;
    }
    const summary = ROWS.map((r) => `${RARITY_LABELS[r.rarity]} ${odds[r.rarity]}%`).join(', ');
    Modal.confirm({
      title: 'Change the card rarity odds?',
      icon: <ExclamationCircleOutlined />,
      content: `${summary}. Every server uses these within seconds for new match drops and other rolled cards. Cards players already have are not changed.`,
      onOk: async () => {
        setSaving(true);
        try {
          const res = await adminApi.updateCardRarityWeights({
            cardRarityWeights: Object.fromEntries(
              ROWS.map((r) => [r.rarity, odds[r.rarity] as number])
            ),
          });
          const weights = (res.data as CardGenerationConfig)?.cardRarityWeights;
          if (weights) {
            setOdds(weights);
            setSaved(weights);
          }
          message.success('Card rarity odds updated');
        } catch (err: unknown) {
          const apiErr = err as { response?: { data?: { message?: string | string[] } } };
          const apiMsg = apiErr?.response?.data?.message;
          message.error(
            (Array.isArray(apiMsg) ? apiMsg.join(', ') : apiMsg) ||
              'Failed to update the card rarity odds'
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={5}>Card Rarity Odds</Title>
        <Text type="secondary">
          The chance that a card comes out at each rarity when nothing else sets it: match drops,
          level-up player drops, stump rewards and store items with no card rarity. Daily Drop,
          Challenger, league prizes and FTUE cards set their own rarity and are not affected. The
          four must add up to 100%. Default: 60 / 25 / 12 / 3.
        </Text>
      </div>

      {loadError && <Alert type="warning" showIcon message={loadError} />}

      <Table<{ rarity: string; color: string }>
        rowKey="rarity"
        pagination={false}
        size="small"
        dataSource={ROWS}
        columns={[
          {
            title: 'Rarity',
            key: 'label',
            width: 160,
            render: (_: unknown, row) => <Tag color={row.color}>{RARITY_LABELS[row.rarity]}</Tag>,
          },
          {
            title: 'Odds',
            key: 'odds',
            width: 160,
            render: (_: unknown, row) => (
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                precision={2}
                addonAfter="%"
                disabled={!editable}
                value={odds[row.rarity]}
                onChange={(v) =>
                  setOdds((prev) => ({
                    ...prev,
                    [row.rarity]: typeof v === 'number' ? v : undefined,
                  }))
                }
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Means',
            key: 'means',
            render: (_: unknown, row) => {
              const v = odds[row.rarity];
              return typeof v === 'number' ? <Text type="secondary">{oneInN(v)}</Text> : null;
            },
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Text strong>Total</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <Text strong type={problem ? 'danger' : undefined}>
                {total}%
              </Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2}>
              {problem && loaded && <Text type="danger">{problem}</Text>}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <Space>
        <Button
          type="primary"
          size="large"
          onClick={handleSave}
          loading={saving}
          disabled={!editable || !!problem || !changed}
        >
          Save Rarity Odds
        </Button>
        {changed && editable && (
          <Button onClick={() => setOdds(saved)} disabled={saving}>
            Undo changes
          </Button>
        )}
      </Space>
    </Space>
  );
};
